import { useEffect, useState } from 'react'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import { useSubmitRcpQuote } from '@/api/accounting-dealer/insurance'
import { useReadUserProfile } from '@/api/user-manager'
import { PageHeader } from '@/components/page-header'
import { useCompany } from '@/context/companies'
import { formatTaxId, toast } from '@/helpers'
import { routes } from '@/router'
import { Company, Deal } from '@/types'
import { FORM_STEPS } from './constants'
import {
  rcpAddressSchema,
  rcpQuoteDefaultValues,
  rcpQuoteFormSchema,
  RcpAddressFormData,
  RcpQuoteFormData,
  STEP_FIELDS,
} from './schema'
import { StepCadastro } from './step-cadastro'
import { StepQuestionario } from './step-questionario'
import { StepSeguro } from './step-seguro'
import type {
  RcpQuoteAddressPayload,
  SubmitRcpQuotePayload,
} from '@/api/accounting-dealer/insurance'

export function RcpQuotePage() {
  const navigate = useNavigate()
  const { company, deal } = useCompany()
  const { data: user } = useReadUserProfile()
  const [activeStep, setActiveStep] = useState(0)
  const [submitted, setSubmitted] = useState(false)

  const form = useForm<RcpQuoteFormData>({
    resolver: zodResolver(rcpQuoteFormSchema),
    defaultValues: rcpQuoteDefaultValues,
  })

  const { mutateAsync: submitQuote, isPending } = useSubmitRcpQuote({
    onSuccess: () => {
      setSubmitted(true)
    },
  })

  useEffect(() => {
    const fullName = user
      ? [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
      : ''
    const email = user?.alias.find((alias) => alias.type === 'email')?.data
    const phone = user?.alias.find((alias) => alias.type === 'phone')?.data
    const taxId = resolvePrefillTaxId(company, deal, fullName)

    if (fullName && !form.getValues('full_name')) {
      form.setValue('full_name', fullName)
    }
    if (email && !form.getValues('email')) {
      form.setValue('email', email)
    }
    if (phone && !form.getValues('phone')) {
      form.setValue('phone', phone)
    }
    if (taxId && !form.getValues('tax_id')) {
      form.setValue('tax_id', formatTaxId(taxId))
    }
  }, [company, deal, form, user])

  const handleNext = async () => {
    const isValid = await form.trigger(STEP_FIELDS[activeStep])
    if (!isValid) return

    if (activeStep < FORM_STEPS.length - 1) {
      setActiveStep((step) => step + 1)
    }
  }

  const handleBack = () => {
    setActiveStep((step) => Math.max(step - 1, 0))
  }

  const onSubmit = async (data: RcpQuoteFormData) => {
    await submitQuote({
      companyKey: company.key,
      data: toSubmitPayload(data),
    })
  }

  const onInvalid = (errors: FieldErrors<RcpQuoteFormData>) => {
    const stepWithError = STEP_FIELDS.findIndex((fields) =>
      fields.some((field) => errors[field]),
    )

    if (stepWithError >= 0) {
      setActiveStep(stepWithError)
    }

    toast.error('Revise os campos obrigatórios para enviar a ficha.')
  }

  return (
    <>
      <PageHeader rollbackRoute={routes.accounting.insurance}>
        Simulação - Seguro de Responsabilidade Civil para médicos
      </PageHeader>

      {submitted ? (
        <Box pb={4}>
          <Alert
            severity="success"
            sx={{
              py: 2,
              alignItems: 'center',
              '& .MuiAlert-message': { fontSize: '1.05rem', lineHeight: 1.5 },
            }}
          >
            Simulação solicitada com sucesso! Você receberá em breve um retorno
            por e-mail e WhatsApp
          </Alert>
          <Box mt={3}>
            <Button onClick={() => navigate(routes.accounting.insurance)}>
              Voltar para seguros
            </Button>
          </Box>
        </Box>
      ) : (
        <FormProvider {...form}>
          <Box
            component="form"
            onSubmit={form.handleSubmit(onSubmit, onInvalid)}
            pb={4}
          >
            <Typography color="text.secondary" mb={3}>
              Preencha os campos abaixo. Todos os campos desta ficha são
              necessários para gerar a cotação.
            </Typography>

            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              {FORM_STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {activeStep === 0 && <StepCadastro />}
            {activeStep === 1 && <StepQuestionario />}
            {activeStep === 2 && <StepSeguro />}

            <Box display="flex" justifyContent="space-between" gap={2} mt={4}>
              <Button
                type="button"
                variant="outlined"
                onClick={handleBack}
                disabled={activeStep === 0 || isPending}
              >
                Voltar
              </Button>

              {activeStep < FORM_STEPS.length - 1 ? (
                <Button type="button" onClick={handleNext}>
                  Continuar
                </Button>
              ) : (
                <LoadingButton type="submit" loading={isPending}>
                  Simular
                </LoadingButton>
              )}
            </Box>
          </Box>
        </FormProvider>
      )}
    </>
  )
}

function resolvePrefillTaxId(
  company: Company,
  deal: Deal,
  userFullName: string,
): string {
  const partnerships = company.partnerships ?? []
  const normalizedName = userFullName.toLowerCase()

  const matchedByName = normalizedName
    ? partnerships.find(
        ({ partner }) => partner.name?.trim().toLowerCase() === normalizedName,
      )
    : undefined
  const mainPartnership = partnerships.find(({ is_main }) => is_main)
  const firstWithTaxId = partnerships.find(({ partner }) => partner.tax_id)

  return (
    matchedByName?.partner.tax_id ||
    mainPartnership?.partner.tax_id ||
    firstWithTaxId?.partner.tax_id ||
    deal.deal_lead?.tax_id ||
    ''
  )
}

function toSubmitPayload(data: RcpQuoteFormData): SubmitRcpQuotePayload {
  return {
    tax_id: data.tax_id,
    full_name: data.full_name,
    has_social_name: data.has_social_name,
    social_name: data.has_social_name ? data.social_name : undefined,
    phone_type: data.phone_type,
    phone: data.phone,
    insurance_kind: data.insurance_kind,
    previous_company_name:
      data.insurance_kind === 'renewal'
        ? data.previous_company_name?.trim()
        : undefined,
    continuity_date:
      data.insurance_kind === 'renewal' ? data.continuity_date : undefined,
    professional_categories: data.professional_categories,
    specialties: data.specialties,
    had_claims_last_5_years: data.had_claims_last_5_years,
    knows_future_claim_risk: data.knows_future_claim_risk,
    has_active_professional_registration:
      data.has_active_professional_registration,
    email: data.email,
    marital_status: data.marital_status,
    sex: data.sex,
    is_politically_exposed: data.is_politically_exposed,
    lives_in_brazil: data.lives_in_brazil,
    coverages: data.coverages,
    residential_address: toAddressPayload(data.residential_address),
    use_residential_as_billing: data.use_residential_as_billing,
    billing_address: data.use_residential_as_billing
      ? undefined
      : toAddressPayload(rcpAddressSchema.parse(data.billing_address)),
  }
}

function toAddressPayload(address: RcpAddressFormData): RcpQuoteAddressPayload {
  return {
    zip_code: address.zip_code,
    street: address.street,
    number: address.no_number ? 'S/N' : address.number,
    complement: address.complement || undefined,
    district: address.district,
    city: address.city,
    state_code: address.state_code,
  }
}
